import { PlusIcon } from '@heroicons/react/24/outline';

import { useState } from 'react';

import { Button, IconButton, Text } from '@/design-system';
import FilterIcon from '@/icons/FilterIcon.svg?react';
import TrashIcon from '@/icons/TrashIcon.svg?react';
import { cn } from '@/utils/tailwind';

import { Popover, PopoverContent, PopoverTrigger } from '../../Popover';
import {
  ExampleFilter,
  ExamplesSearchModel,
} from '../types/ExamplesSearchModel';
import { countNumQueries } from '../utils';
import { ExampleFilterBarOperands } from './ExampleFilterBarOperands';

const pluralRules = new Intl.PluralRules('en-US');

function ExampleFilterDialogContent(props: {
  commonMetadata: Record<string, unknown[]>;
  value: ExamplesSearchModel;
  filters: ExampleFilter[];
  onChange: (value: ExampleFilter[]) => void;
}) {
  const numQueries = countNumQueries(props.value);

  return (
    <div className="group flex flex-col items-stretch border-tertiary p-3">
      <div className="flex flex-col pb-1">
        <div className="flex items-start justify-between">
          <Text>Filters</Text>

          <div className="flex items-start gap-1 opacity-0 transition-all focus-within:opacity-100 group-hover:opacity-100 [&:has([data-state=open])]:opacity-100">
            {numQueries > 0 && (
              <IconButton
                variant="outlined"
                color="secondary"
                size="sm"
                icon={TrashIcon}
                label="Clear filters"
                tooltipProps={{
                  side: 'top',
                }}
                onClick={() => {
                  props.onChange([]);
                }}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <ExampleFilterBarOperands
          currentFilters={props.filters}
          commonMetadata={props.commonMetadata}
          onChange={props.onChange}
        />

        <div className="flex">
          <Button
            variant="normal"
            color="secondary"
            size="sm"
            leftDecorator={PlusIcon}
            onClick={() => {
              props.onChange([...props.filters, {}]);
            }}
          >
            Add filter
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ExamplesFilterBar(props: {
  commonMetadata: Record<string, unknown[]>;
  value: ExamplesSearchModel;
  filters: ExampleFilter[];
  onChange: (value: ExampleFilter[]) => void;
}) {
  const nActive = countNumQueries(props.value);
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="normal"
          color="secondary"
          size="sm"
          leftDecorator={FilterIcon}
        >
          {nActive === 0 ? (
            <Text>Filters</Text>
          ) : (
            <Text>
              {
                {
                  one: `1 filter`,
                  two: `${nActive} filters`,
                  few: `${nActive} filters`,
                  other: `${nActive} filters`,
                }[pluralRules.select(nActive)]
              }
            </Text>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align={'start'}
        className={cn('z-10 w-auto min-w-[300px] overflow-hidden p-0')}
        avoidCollisions={false}
        side={'bottom'}
      >
        <ExampleFilterDialogContent
          commonMetadata={props.commonMetadata}
          value={props.value}
          filters={props.filters}
          onChange={props.onChange}
        />
      </PopoverContent>
    </Popover>
  );
}
